import { AdminContainer } from "../../components/layouts";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography
} from "@material-ui/core";
import React, { useEffect, useState, Suspense } from "react";
import { useParams } from "react-router";
import api from "../../api";
import { useLoading } from "../../hooks/loading";
import { toast } from "react-toastify";
import { ApplicantsList } from "./ApplicantsList";
import AuditLogsTable from "../../components/AuditLogsTable";
import DetailSection from "../../components/DetailSection";
import InlineEditable from "../../components/InlineEditable";
import AdminActionLinks from "../../components/AdminActionLinks";
import SafeExternalLink from "../../lithic/SafeExternalLink";
import Link from "../../components/Link";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import { Autocomplete } from "@material-ui/lab";
import Money from "../../lithic/Money";
import { InviteesList } from "./InviteesList";

const AuditLogs = React.lazy(() => import("../../components/AuditLogs"));

export default function ApplicationDetailPage() {
  const { id } = useParams();
  const { startLoading, stopLoading } = useLoading();
  const [application, setApplication] = useState(null);

  const fetchApplication = () => {
    startLoading();
    api
      .getApplication(id)
      .then((result) => setApplication(result.data))
      .catch((err) => toast.error(err.message))
      .finally(() => {
        stopLoading();
      });
  };

  useEffect(() => {
    fetchApplication();
  }, [id]);

  if (!application) return <div>Loading…</div>;

  return (
    <AdminContainer variant="list">
      <Typography variant="h4">Application Details</Typography>
      <Typography variant="body1">
        Application Status: {application.status}
      </Typography>
      <DetailSection title="Partner">
        <Link to={`/partners/${application.partner.id}`}>
          {application.partner.name}
        </Link>
      </DetailSection>
      <DetailSection title="Verification template">
        <SafeExternalLink href={application.verificationTemplate.url}>
          {application.verificationTemplate.name}
        </SafeExternalLink>
      </DetailSection>
      <DetailSection title="Applicants">
        <ApplicantsList
          applicants={application.applicants}
          onRefresh={fetchApplication}
        />
      </DetailSection>
      <DetailSection title="Invitees">
        <InviteesList invitees={application.pendingInvitations} />
      </DetailSection>
      <DetailSection title="Desired Move-In Date">
        <InlineEditable
          label="Desired Move-In Date"
          value={application.desiredMoveInDate}
          inputType="date"
          onCommit={(date) =>
            api
              .updateApplication(application.id, {
                desiredMoveInDate: date ? date.format("YYYY-MM-DD") : null
              })
              .then((resp) => setApplication(resp.data))
          }
        />
      </DetailSection>
      <DetailSection title="Unit">
        <Typography>Current Unit: {application.unit.title}</Typography>
        <Typography>Address Unit: {application.unit.address}</Typography>
        <Typography>
          Monthly Rent Amount:{" "}
          <Money>{application.unit.monthlyRentAmount}</Money>
        </Typography>
        <ChangeUnitButton
          partnerId={application.partner.id}
          applicationId={application.id}
          applicationUnit={application.unit}
        />
      </DetailSection>
      <DetailSection title="Audit Logs">
        <Suspense fallback={<div>Loading...</div>}>
          <AuditLogs
            auditableId={application.id}
            auditableType="BoomScreen::Application"
          />
        </Suspense>
      </DetailSection>
      <AdminActionLinks
        links={application.adminActionLinks}
        onActionSuccess={(resp) => setApplication(resp.data)}
      />
      {/* Temporarily. Waiting for the backend update */}
      <DetailSection>
        <Typography variant="h6" gutterBottom color="primary">
          Portal
        </Typography>
        <SafeExternalLink href="/">Open Portal</SafeExternalLink>
      </DetailSection>
    </AdminContainer>
  );
}

function ChangeUnitButton({ partnerId, applicationId, applicationUnit }) {
  const [openModal, setOpenModal] = useState(false);
  const [units, setUnits] = useState(null);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [newUnit, setNewUnit] = useState(applicationUnit);

  // Units are fetched lazily when the modal opens — most application views
  // never change the unit, so we avoid loading the partner's full inventory
  // on every page load.
  const openAndLoadUnits = () => {
    setOpenModal(true);

    if (units || unitsLoading) return;

    setUnitsLoading(true);
    api
      .getPartnerUnits({ id: partnerId })
      .then((result) => setUnits(result.data.items))
      .catch((err) => toast.error(err.message))
      .finally(() => setUnitsLoading(false));
  };

  return (
    <Box mt={1}>
      <Button color="primary" variant="contained" onClick={openAndLoadUnits}>
        Change Unit
      </Button>

      <ConfirmationDialog
        title="Change Unit"
        open={openModal}
        onCancel={() => setOpenModal(false)}
        onConfirm={() => {
          api
            .changeApplicationUnit(applicationId, { unit_id: newUnit.id })
            .then(() => {
              toast.success("Application updated successfully!");
              setOpenModal(false);
            })
            .catch((err) => toast.error(err.message));
        }}
        text="Are you sure?"
        cancelProps={{ children: "Go back" }}
        confirmProps={{
          children: "Update",
          disabled: newUnit?.id === applicationUnit.id || !newUnit
        }}
      >
        {unitsLoading || !units ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            style={{ width: 300, padding: 16 }}
          >
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Autocomplete
            id="combo-box-demo"
            options={units}
            getOptionLabel={(value) => `${value.id} - ${value.title}`}
            onChange={(event, newValue) => {
              setNewUnit(newValue);
            }}
            style={{ width: 300 }}
            renderInput={(params) => (
              <TextField
                variant="outlined"
                label="Unit Search"
                {...params}
                fullWidth
              />
            )}
          />
        )}
      </ConfirmationDialog>
    </Box>
  );
}
